import { FormEvent, useEffect, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { useCurrentHealthCenter } from '../../components/AppShell'
import {
  TreatmentAttributes,
  TreatmentRecord,
  createTreatment,
  fetchTreatment,
  fetchTreatmentTemplates,
  updateTreatment,
} from '../../api/treatments'
import { fetchPatient } from '../../api/patients'
import {
  Button,
  CurrencyField,
  DateField,
  Panel,
  PanelBody,
  PanelHeading,
  RadioGroup,
  SelectField,
  TextAreaField,
  TextField,
} from '../../components/common'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { formatMoney } from '../../lib/currency'

type FormValues = {
  date: string
  name: string
  code: string
  price: string
  description: string
  note: string
  fee: string
  timeless: boolean
}

const EMPTY_FORM: FormValues = { date: '', name: '', code: '', price: '', description: '', note: '', fee: '', timeless: false }

function autosaveKey(): string {
  return `hc-autosave:${window.location.pathname}`
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function getFormFromTreatment(treatment: TreatmentRecord): FormValues {
  return {
    date: treatment.date ?? today(),
    name: treatment.name ?? '',
    code: treatment.code ?? '',
    price: formatMoney(treatment.price ?? ''),
    description: treatment.description ?? '',
    note: treatment.note ?? '',
    fee: treatment.fee === null ? '' : String(treatment.fee),
    timeless: treatment.timeless === true,
  }
}

function restoreDraft(): FormValues {
  const blank: FormValues = { ...EMPTY_FORM, date: today() }
  const stored = window.localStorage.getItem(autosaveKey())
  return stored ? { ...blank, ...(JSON.parse(stored) as Partial<FormValues>) } : blank
}

export async function loader({ params }: LoaderFunctionArgs) {
  const healthCenterId = Number(params.healthCenterId)
  const patientId = params.patientId as string
  const [templates, patient, treatment] = await Promise.all([
    fetchTreatmentTemplates(healthCenterId),
    fetchPatient(healthCenterId, patientId),
    params.treatmentId ? fetchTreatment(healthCenterId, patientId, params.treatmentId) : null,
  ])
  return { templates, patient, treatment }
}

export default function TreatmentFormPage() {
  const healthCenter = useCurrentHealthCenter()
  const navigate = useNavigate()
  const params = useParams()
  const patientId = params.patientId ?? ''
  const treatmentId = params.treatmentId
  const { templates, patient, treatment } = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const [values, setValues] = useState<FormValues>(() => (treatment ? getFormFromTreatment(treatment) : restoreDraft()))
  const [errors, setErrors] = useState<readonly string[]>([])
  const [fromTemplate, setFromTemplate] = useState(false)

  useEffect(() => {
    if (!treatmentId) {
      window.localStorage.setItem(autosaveKey(), JSON.stringify(values))
    }
  }, [treatmentId, values])

  if (!healthCenter) {
    return null
  }

  const set = (field: keyof FormValues, value: string | boolean) => setValues({ ...values, [field]: value })

  const selectTemplate = (code: string) => {
    const template = templates.find((candidate) => candidate.code === code)
    if (!template) {
      return
    }
    setFromTemplate(true)
    trackEvent('Treatment Template Applied', { template_id: template.id })
    setValues({
      ...values,
      name: template.name ?? '',
      code: template.code ?? '',
      price: template.price ?? '',
      description: template.description ?? '',
      note: template.note ?? '',
      timeless: template.timeless === true,
    })
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const attributes: TreatmentAttributes = {
      date: values.date,
      name: values.name,
      code: values.code,
      price: values.price,
      description: values.description,
      note: values.note,
      fee: values.fee,
      timeless: values.timeless,
    }
    try {
      if (treatmentId) {
        await updateTreatment(healthCenter.id, patientId, treatmentId, attributes)
      } else {
        await createTreatment(healthCenter.id, patientId, attributes)
        window.localStorage.removeItem(autosaveKey())
      }
      trackEvent(treatmentId ? 'Treatment Updated' : 'Treatment Created', { from_template: fromTemplate, is_timeless: values.timeless })
      navigate(`/health_centers/${healthCenter.id}/patients/${patientId}/treatments`)
    } catch (error) {
      if (error instanceof ApiError && error.body && typeof error.body === 'object') {
        trackEvent('Form Save Failed', { form: 'treatment', reason: 'validation', status_code: error.status, fields: Object.keys(error.body) })
        setErrors(Object.entries(error.body as Record<string, string[]>).map(([field, messages]) => `${field} ${messages.join(', ')}`))
        return
      }
      throw error
    }
  }

  return (
    <Panel>
      <PanelHeading>
        <h1>{treatmentId ? 'Modificando Tratamiento' : `Nuevo Tratamiento para ${patient.name ?? ''}`}</h1>
      </PanelHeading>
      <PanelBody className="container">
        <form onSubmit={(event) => void handleSubmit(event)}>
          {errors.length > 0 && (
            <div id="error_explanation">
              <ul>
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="row">
            <div className="col-xs-12 col-md-2">
              <DateField label="Fecha" labelStyle="plain" value={values.date} onChange={(value) => set('date', value)} />
            </div>
            <div className="col-xs-12 col-md-10">
              <SelectField
                label="Tratamiento"
                labelStyle="plain"
                value={values.code || (templates[0]?.code ?? '')}
                onChange={selectTemplate}
                options={templates.map((template) => ({ value: template.code ?? '', label: `${template.name} : ${template.code}` }))}
              />
            </div>
          </div>

          <TextField label="Nombre" labelStyle="plain" value={values.name} onChange={(value) => set('name', value)} />
          <TextField label="Codigo" labelStyle="plain" value={values.code} onChange={(value) => set('code', value)} />
          <RadioGroup
            name="timeless"
            inline={false}
            value={values.timeless ? 'true' : 'false'}
            onChange={(value) => set('timeless', value === 'true')}
            options={[
              { value: 'false', label: 'Precio total del tratamiento (duración fija)' },
              { value: 'true', label: 'Precio por sesión (duración indefinida)' },
            ]}
          />
          <CurrencyField label="Precio" value={values.price} onChange={(value) => set('price', value)} />
          <TextAreaField
            label="Descripción"
            labelStyle="plain"
            rows={8}
            value={values.description}
            onChange={(value) => set('description', value)}
          />
          <TextAreaField label="Nota" labelStyle="plain" rows={6} value={values.note} onChange={(value) => set('note', value)} />
          <TextField
            label="Número de cuotas"
            labelStyle="plain"
            type="number"
            value={values.fee}
            onChange={(value) => set('fee', value)}
          />
          <div className="actions">
            <Button type="submit">Crear</Button>
          </div>
        </form>
      </PanelBody>
    </Panel>
  )
}
